import { useMemo } from "react";
import type { MatrixClient, Room } from "matrix-js-sdk";
import { Avatar, Text } from "../../theme/primitives";
import { avatarHttpUrl, listRoomMembers } from "../../matrix/rooms";
import { SidePanel } from "./SidePanel";
import { useRoomMemberProfiles } from "./useRoomMemberProfiles";

interface GroupInfoPanelProps {
  client: MatrixClient;
  room: Room;
  onClose: () => void;
}

/** Group-info drawer: group name, member count, and the member list (+ company). */
export function GroupInfoPanel({ client, room, onClose }: GroupInfoPanelProps) {
  const members = useMemo(() => listRoomMembers(room), [room]);
  const profiles = useRoomMemberProfiles(client, room);
  const me = client.getUserId();

  return (
    <SidePanel title="Group info" onClose={onClose}>
      <div style={{ padding: "var(--space-xl)", textAlign: "center", borderBottom: "1px solid var(--color-border)" }}>
        <div
          style={{
            width: 88,
            height: 88,
            margin: "0 auto 8px",
            borderRadius: "var(--radius-pill)",
            background: "var(--color-header-bg)",
            color: "var(--color-header-text)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 34,
          }}
        >
          👥
        </div>
        <Text weight="bold" size="xl">{room.name || "Group chat"}</Text>
        <div><Text size="sm" muted>{members.length} members</Text></div>
      </div>
      <div>
        <div style={{ padding: "var(--space-sm) var(--space-lg)" }}>
          <Text size="sm" muted>MEMBERS</Text>
        </div>
        {members.map((m) => {
          const profile = profiles.get(m.userId);
          const company = profile?.meta?.company?.value ?? profile?.company ?? undefined;
          const avatarSrc = profile?.meta?.avatar?.value || avatarHttpUrl(client, m.avatarMxc);
          return (
            <div
              key={m.userId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-md)",
                padding: "var(--space-sm) var(--space-lg)",
              }}
            >
              <Avatar name={m.displayName} src={avatarSrc} size={40} />
              <div style={{ minWidth: 0 }}>
                <Text weight="medium" truncate>
                  {m.displayName}
                  {m.userId === me ? " (You)" : ""}
                </Text>
                <div>
                  <Text size="sm" muted>{company || m.userId}</Text>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </SidePanel>
  );
}
